/* HotspotsContainer: Singleton for storing all hotspots */
export class HotspotsContainer {
  constructor() {
    if (HotspotsContainer._instance) {
      return HotspotsContainer._instance;
    }
    this._hotspots = [];
    this._proximityHotspots = [];
    this._globalProximityConfig = null;
    HotspotsContainer._instance = this;
  }

  /**
   * Get the singleton instance
   * @returns {HotspotsContainer}
   */
  static getInstance() {
    return new HotspotsContainer();
  }

  /**
   * Set global proximity configuration
   * @param {Object} config - Global proximity config with enabled and threshold
   */
  setGlobalProximityConfig(config) {
    this._globalProximityConfig = config;
    // Re-evaluate all hotspots when global config changes
    this._updateProximityHotspots();
  }

  /**
   * Get effective proximity config for a hotspot
   * @param {HotspotConstructor} hotspot
   * @returns {Object} Effective proximity configuration
   */
  _getEffectiveProximityConfig(hotspot) {
    // If global config is enabled, it overrides hotspot config
    if (this._globalProximityConfig?.enabled) {
      return this._globalProximityConfig;
    }
    // Otherwise use hotspot's individual config
    return hotspot?.userData?.proximity || { enabled: false };
  }

  /**
   * Re-evaluate proximity hotspots based on current configs
   */
  _updateProximityHotspots() {
    this._proximityHotspots = [];
    this._hotspots.forEach(hotspot => {
      const effectiveConfig = this._getEffectiveProximityConfig(hotspot);
      if (effectiveConfig.enabled && hotspot?.visualObject?.material) {
        this._proximityHotspots.push(hotspot);
        hotspot.visualObject.material.transparent = true; // Enable transparency
        hotspot.visualObject.material.opacity = 0;        // Start invisible
      }
    });
  }

  /**
   * Add a hotspot to the container
   * @param {HotspotConstructor} hotspot
   */
  add(hotspot) {
    this._hotspots.push(hotspot);
    
    const effectiveConfig = this._getEffectiveProximityConfig(hotspot);
    if (effectiveConfig.enabled && hotspot?.visualObject?.material) {
      this._proximityHotspots.push(hotspot);
      hotspot.visualObject.material.transparent = true; // Enable transparency
      hotspot.visualObject.material.opacity = 0;        // Start invisible
    }
  }

  /**
   * Get all hotspots
   * @returns {HotspotConstructor[]}
   */
  getAll() {
    return [...this._hotspots];
  }

  getProximityHotspots() {
    return [...this._proximityHotspots];
  }

  /**
   * Get effective proximity config for a hotspot (for external use)
   * @param {HotspotConstructor} hotspot
   * @returns {Object} Effective proximity configuration
   */
  getEffectiveProximityConfig(hotspot) {
    return this._getEffectiveProximityConfig(hotspot);
  }

  /**
   * Count of hotspots
   * @returns {number}
   */
  count() {
    return this._hotspots.length;
  }
}
